/**
 * Customer API Service
 * Handles customer CRUD operations
 */

import api from './api';
import { Customer } from '../types';

export interface CustomerListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  source?: 'ALL' | 'ZOHO' | 'LOCAL';
  status?: 'ALL' | 'ACTIVE' | 'ON_HOLD' | 'INACTIVE';
  category?: string;
  customerType?: string;
  zohoLinked?: boolean;
  paymentPolicyType?: string;
  outstandingOnly?: boolean;
}

export interface CustomerListResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  items: Customer[];
}

export const customerApi = {
  /**
   * Get all customers
   */
  getAll: async (): Promise<Customer[]> => {
    const response = await api.get('/customers', {
      params: {
        all: true,
      },
    });
    return response.data?.data || [];
  },

  /**
   * Get paginated customer summaries for the Clients page
   */
  listPaged: async (params: CustomerListParams = {}): Promise<CustomerListResponse> => {
    const response = await api.get('/customers', {
      params: {
        page: params.page || 1,
        pageSize: params.pageSize || 50,
        search: params.search || undefined,
        source: params.source && params.source !== 'ALL' ? params.source : undefined,
        status: params.status && params.status !== 'ALL' ? params.status : undefined,
        category: params.category || undefined,
        customerType: params.customerType || undefined,
        zohoLinked: typeof params.zohoLinked === 'boolean' ? params.zohoLinked : undefined,
        paymentPolicyType: params.paymentPolicyType || undefined,
        outstandingOnly: params.outstandingOnly ? 'true' : undefined,
      },
    });

    return {
      totalCount: Number(response.data?.totalCount || 0),
      page: Number(response.data?.page || params.page || 1),
      pageSize: Number(response.data?.pageSize || params.pageSize || 50),
      items: response.data?.items || response.data?.data || [],
    };
  },

  /**
   * Get a specific customer by ID
   */
  getById: async (id: string): Promise<Customer> => {
    const response = await api.get(`/customers/${id}`);
    return response.data?.data;
  },

  /**
   * Create a new customer
   */
  create: async (customer: Partial<Customer>): Promise<Customer> => {
    const response = await api.post('/customers', customer);
    return response.data?.data;
  },

  /**
   * Update a customer
   */
  update: async (id: string, updates: Partial<Customer>): Promise<Customer> => {
    const response = await api.patch(`/customers/${id}`, updates);
    return response.data?.data;
  },

  /**
   * Assign or remove a customer category (discount group)
   */
  setCategory: async (id: string, categoryId?: string | null, applyDefaults?: boolean): Promise<Customer> => {
    const response = await api.patch(`/customers/${id}/category`, {
      categoryId: categoryId ?? null,
      applyDefaults: !!applyDefaults,
    });
    return response.data?.data;
  },

  /**
   * Refresh a Zoho-linked customer from Zoho Books
   */
  refreshFromZoho: async (id: string): Promise<Customer> => {
    const response = await api.get(`/customers/${id}/zoho-refresh`);
    return response.data?.data;
  },

  /**
   * Refresh Zoho balances for all Zoho-linked customers
   */
  refreshAllZoho: async (): Promise<{ updated: number }> => {
    const response = await api.post('/customers/zoho/refresh-all');
    return response.data?.data || { updated: response.data?.updated || 0 };
  },

  /**
   * Fetch Zoho Books invoice + estimate summaries for a customer
   */
  getZohoFinance: async (id: string): Promise<{ invoices: any; estimates: any }> => {
    const response = await api.get(`/customers/${id}/zoho-finance`);
    return response.data?.data;
  },

  /**
   * Delete a customer (soft delete)
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/customers/${id}`);
  },
};

// ── Supporting types ────────────────────────────────────────────────────────

export interface CustomerContactMethod {
  id: string;
  customerId: string;
  type: 'PHONE' | 'EMAIL' | 'WHATSAPP' | 'OTHER';
  value: string;
  label?: string | null;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerFollowUp {
  id: string;
  customerId: string;
  assignedUserId?: string | null;
  note: string;
  dueAt: string;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DuplicateCandidate {
  id: string;
  name: string;
  email?: string | null;
  phoneNumber?: string | null;
  status: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
}

export interface CustomerProfile {
  customer: Customer & { categoryAssignments?: any[] };
  contactMethods: CustomerContactMethod[];
  followUps: CustomerFollowUp[];
  externalSources?: Array<{
    id: string;
    provider: 'GOOGLE';
    accountKey: string;
    sourceSurface?: 'CONTACT' | 'OTHER_CONTACT';
    matchConfidence?: 'HIGH' | 'MEDIUM' | 'LOW' | null;
    matchReason?: string | null;
    isPrimarySource: boolean;
    createdAt: string;
    updatedAt: string;
    source: {
      id: string;
      externalId: string;
      etag?: string | null;
      deletedAt?: string | null;
      lastSyncedAt?: string | null;
      rawSnapshot?: any;
    } | null;
  }>;
  whatsappThreads: Array<{
    id: string; status: string; needsReply: boolean; isOverdue: boolean;
    lastMessageAt: string; customerName?: string | null; assignedUserId?: string | null;
    Number?: { label: string } | null;
  }>;
  emailThreads: Array<{
    id: string; status: string; subject?: string | null; customerEmail?: string | null; lastMessageAt: string;
  }>;
  quotes: Array<{
    id: string; status: string; totalUSD: number; createdAt: string;
    legs?: Array<{ departureAirport: string; arrivalAirport: string }>;
  }>;
  missions: Array<{
    id: string; status: string; totalUSD: number; createdAt: string;
    departureAirport?: string | null; arrivalAirport?: string | null;
  }>;
}

export interface GoogleDiscoveredAccount {
  accountKey: string;
  gmailEnabled: boolean;
  syncEnabled: boolean;
  peopleReadEnabled: boolean;
  peopleWriteEnabled: boolean;
  otherContactsReadEnabled: boolean;
  refreshTokenAvailable: boolean;
  mailboxEmail?: string | null;
  scopes: string[];
  syncState?: {
    syncEnabled?: boolean;
    otherContactsReadEnabled?: boolean;
    nextSyncToken?: string | null;
    otherContactsNextSyncToken?: string | null;
    lastFullSyncAt?: string | null;
    lastIncrementalSyncAt?: string | null;
    lastOtherContactsFullSyncAt?: string | null;
    lastOtherContactsIncrementalSyncAt?: string | null;
    lastError?: string | null;
    updatedAt?: string | null;
  } | null;
}

export interface GoogleContactSyncRun {
  id: string;
  accountKey: string;
  sourceSurface: 'CONTACT' | 'OTHER_CONTACT';
  syncType: 'FULL' | 'INCREMENTAL';
  status: 'QUEUED' | 'RUNNING' | 'SUCCESS' | 'FAILED' | 'PARTIAL' | 'CANCELLED';
  isDryRun?: boolean;
  scannedCount: number;
  createdCustomerCount: number;
  linkedCustomerCount: number;
  updatedCustomerCount: number;
  duplicateRiskCount: number;
  skippedUnmatchedCount?: number;
  pagesProcessed?: number;
  heartbeatAt?: string | null;
  syncTokenUsed?: string | null;
  currentPageToken?: string | null;
  nextPageToken?: string | null;
  nextSyncTokenCandidate?: string | null;
  errorMessage?: string | null;
  sampleErrors?: Array<{ externalId: string; message: string }>;
  sampleDuplicateRisks?: Array<{ externalId: string; primaryName?: string | null; emails: string[]; phones: string[]; customerIds: string[] }>;
  startedAt: string;
  finishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GoogleDuplicateRiskItem {
  id: string;
  accountKey: string;
  sourceSurface: 'CONTACT' | 'OTHER_CONTACT';
  externalId: string;
  deletedAt?: string | null;
  ignoredAt?: string | null;
  lastSyncedAt?: string | null;
  primaryName?: string | null;
  emails: string[];
  phones: string[];
  customerIds: string[];
}

export interface GoogleDuplicateRiskDetail {
  source: any;
  fields: {
    sourceSurface?: 'CONTACT' | 'OTHER_CONTACT';
    primaryName?: string | null;
    company?: string | null;
    emails: string[];
    normalizedEmails?: string[];
    phones: string[];
    normalizedPhoneKeys?: string[];
    duplicateRiskCustomerIds: string[];
    autoLinkBlockedReason?: string | null;
    rawSnapshot?: any;
  };
  candidates: Array<any>;
  candidateDiagnostics?: Array<{
    customerId: string;
    matchedOnEmail?: string | null;
    matchedOnPhone?: string | null;
    normalizedCustomerEmail?: string | null;
    normalizedCustomerPhoneKey?: string | null;
  }>;
  autoLinkBlockedReason?: string | null;
  linkedCustomers: Array<any>;
}

export interface GoogleDiagnosticResult {
  accountKey: string;
  mailboxEmail?: string | null;
  sourceSurface: 'CONTACT' | 'OTHER_CONTACT';
  sourceSurfaceLabel: string;
  resourceName: string;
  primaryName?: string | null;
  normalizedEmails: string[];
  normalizedPhoneKeys: string[];
  linkedCustomers: Array<any>;
  candidateCustomers: Array<any>;
  autoLinkBlockedReason?: string | null;
}
